import Big from 'big.js';
import axios from 'smg/axios';
import { AddCartItemParams } from 'smg/customer-api/carts';
import * as contact from 'smg/customer-api/contact';
import { Session } from 'smg/customer-api/session';
import { HandleRequestError } from 'smg/errors';
import * as filepath from 'smg/filepath';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { Phone } from 'smg/phone';
import { G } from 'smg/querystring';
import * as strings from 'smg/strings';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';
import { PromptQuantityRequest } from '../quotes';

export default async function main() {
    const quoteID = G().Get('quote');
    const orderID = G().Get('order');
    if (quoteID) {
        await carts.AddCartItem({ quoteID }, { promptToCheckout: false });
    }
    if (orderID) {
        await carts.AddCartItem({ orderID }, { promptToCheckout: false });
    }
    const newCartItem = G().Get('new');
    if (newCartItem) {
        try {
            await carts.AddCartItem(JSON.parse(newCartItem) as AddCartItemParams, { promptToCheckout: false });
        } catch (e) {
            HandleRequestError(e);
        }
    }
    const cartID = G().Get('cart');
    if (cartID) {
        await carts.AddCartItem({ cartID }, { promptToCheckout: false });
    }

    const cartCard = document.querySelector('#cart-card') as HTMLElement;

    const cartItemTemplate = document.querySelector('#cart-item-template') as HTMLElement;
    cartItemTemplate.removeAttribute('id');
    cartItemTemplate.parentElement?.removeChild(cartItemTemplate);
    cartItemTemplate.classList.remove('d-none');

    const cartItemLineTemplate = cartItemTemplate.querySelector('.cart-item-line') as HTMLElement;
    cartItemLineTemplate.parentElement?.removeChild(cartItemLineTemplate);

    const cartItemProofTemplate = cartItemTemplate.querySelector('.cart-item-footer-proof') as HTMLElement;
    cartItemProofTemplate.parentElement?.removeChild(cartItemProofTemplate);

    const spinner = document.querySelector('#cart-spinner') as HTMLElement;
    const checkoutButton = document.querySelector('#checkout-button') as HTMLElement;

    const cartEmpty = document.querySelector('#cart-empty') as HTMLElement;
    cartEmpty.parentElement?.removeChild(cartEmpty);
    cartEmpty.classList.remove('d-none');

    const showEmptyCart = () => {
        cartCard.innerHTML = '';
        cartCard.appendChild(cartEmpty);
    };

    const drawCartItem = (cartItem: models.CartItem): HTMLElement => {
        const cartItemEl = cartItemTemplate.cloneNode(true) as HTMLElement;
        cartItemEl.removeAttribute('id');

        const title = cartItemEl.querySelector('.cart-item-title') as HTMLElement;

        const subtotal = cartItemEl.querySelector('.cart-item-subtotal') as HTMLElement;
        subtotal.textContent = DollarFormat(cartItem.total || 0);

        const oldSubtotal = cartItemEl.querySelector('.text-decoration-line-through') as HTMLElement;
        if (cartItem.maxNetworkSubtotal && Big(cartItem.maxNetworkSubtotal).gt(cartItem.subtotal || 0)) {
            oldSubtotal.textContent = DollarFormat(cartItem.maxNetworkSubtotal);
            subtotal.classList.add('text-red');
        } else {
            oldSubtotal.parentElement?.removeChild(oldSubtotal);
        }

        const deleteLink = cartItemEl.querySelector('.cart-item-delete') as HTMLAnchorElement;
        deleteLink.addEventListener('click', e => {
            e.preventDefault();

            let hr: HTMLElement | undefined;
            if (cartItemEl.nextElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = cartItemEl.nextElementSibling as HTMLElement;
            } else if (cartItemEl.previousElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = cartItemEl.previousElementSibling as HTMLElement;
            }

            const els = [cartItemEl];
            if (hr) {
                els.push(hr);
            }

            els.forEach(el => el.classList.add('d-none'));
            carts.DeleteCartItem(cartItem.id || '', cartItem.quantity || 1).
                then(() => {
                    void drawCart();
                    els.forEach(el => el.parentElement?.removeChild(el));
                    if (!carts.GetCurrentCartCount()) {
                        showEmptyCart();
                    }
                }).
                catch(err => {
                    els.forEach(el => el.classList.remove('d-none'));
                    throw err;
                });
        });

        const revisionParent = cartItemEl.querySelector('.cart-item-revision-parent') as HTMLElement;
        const revision = revisionParent.querySelector('.cart-item-revision') as HTMLAnchorElement;
        if (cartItem.quote?.id && revision) {
            revision.href = `${Relref('/revisions')}?id=${cartItem.quote.key || ''}`;
        } else if (revisionParent) {
            revisionParent.parentElement?.removeChild(revisionParent);
        }

        const info = cartItemEl.querySelector('.cart-item-info') as HTMLAnchorElement;
        if (cartItem.quote?.quoteLineItems?.length || cartItem.order?.orderLineItems?.length) {
            info.addEventListener('click', async e => {
                e.preventDefault();
                new Modal({
                    title: `Pricing breakdown for ${title.textContent || 'item'}`,
                    body: await lineitems.Table(cartItem.quote?.quoteLineItems ?? cartItem.order?.orderLineItems ?? []),
                    size: 'modal-lg',
                    cancelText: 'Close',
                });
            });
        } else {
            info.parentElement?.removeChild(info);
        }

        const drawCartItemLine = (lineItem: models.QuoteLineItem | models.OrderLineItem): HTMLElement => {
            const lineItemEl = cartItemLineTemplate.cloneNode(true) as HTMLElement;
            const summaryProof = lineItemEl.querySelector('.cart-item-summary-proof') as HTMLElement;
            const summaryQty = lineItemEl.querySelector('.cart-item-summary-quantity') as HTMLElement;
            const summaryType = lineItemEl.querySelector('.cart-item-summary-type') as HTMLElement;

            const proofLink = lineItemEl.querySelector('.proof-link') as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            const proof = (lineItem as models.QuoteLineItem).quoteProof?.proof || (lineItem as models.OrderLineItem).orderProof?.proof;
            let proofName: string = '';
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id ?? ''}`);

                proofImg.src = proof.thumbnail || proof.main;

                if ((lineItem as models.QuoteLineItem).quoteProof?.number) {
                    proofName = `Proof ${(lineItem as models.QuoteLineItem).quoteProof?.number ?? ''}`;
                } else {
                    proofName = (lineItem as models.OrderLineItem).orderProof?.name || '';
                }
                proofLink.setAttribute('data-caption', proofName);

                proofImg.title = proofName;
                proofImg.alt = proofName;
            } else {
                const lineItemProofHeaders = Array.from(lineItemEl.querySelectorAll('.has-proofs'));
                const proofSubtotalContainer = cartItemEl.querySelector('.proof-subtotal-container') as HTMLElement;
                const lineItemNoProofsHideDash = lineItemEl.querySelector('#no-proofs-hide') as HTMLElement;
                const noProofsItemsHeader = cartItemEl.querySelector('#no-proofs-items-header') as HTMLElement;
                noProofsItemsHeader.textContent = 'Items';
                proofSubtotalContainer.classList.remove('mb-10');
                for (const proofHeader of lineItemProofHeaders) {
                    proofHeader.classList.add('d-none');
                }
                lineItemNoProofsHideDash.classList.remove('xl-dash');
                proofLink.parentElement?.removeChild(proofLink);
            }

            summaryProof.textContent = '';
            summaryQty.textContent = '';
            summaryType.textContent = '';
            if (proofName) {
                if ((lineItem as models.QuoteLineItem).quoteProof?.number) {
                    summaryProof.textContent = `Proof ${(lineItem as models.QuoteLineItem).quoteProof?.number ?? ''}`;
                } else {
                    summaryProof.textContent = (lineItem as models.OrderLineItem).orderProof?.name ?? '';
                }
            }

            const qty = lineItem.quantity || 0;
            const product = (qty === 1 ? lineItem.lineItemModel?.name : lineItem.lineItemModel?.pluralName) || lineItem.lineItem;
            summaryType.textContent = product ?? '';
            summaryQty.textContent = Number(qty).toLocaleString();

            const controls = lineItemEl.querySelector('.cart-item-line-controls') as HTMLElement;
            const changeQty = controls.querySelector('.cart-item-edit-quantity') as HTMLAnchorElement;
            const removeParent = controls.querySelector('.cart-item-line-delete-parent') as HTMLElement;
            const remove = removeParent.querySelector('.cart-item-line-delete') as HTMLAnchorElement;
            if (!lineItem.lineItemModel?.product) {
                controls.parentElement?.removeChild(controls);
            } else {
                if (productCount < 2) {
                    removeParent.parentElement?.removeChild(removeParent);
                }

                if (!lineItem.customerEditable) {
                    remove.addEventListener('click', async e => {
                        e.preventDefault();

                        const sess = await Session().catch(() => undefined);

                        const body = template.Clone('#item-removal-request-modal-form-template');

                        const fullName = body.querySelector('#modal-full-name') as HTMLInputElement;
                        const email = body.querySelector('#modal-email') as HTMLInputElement;
                        const phone = body.querySelector('#modal-phone') as HTMLInputElement;
                        const message = body.querySelector('#modal-comments') as HTMLInputElement;

                        const $reCaptchaPlaceholder = body.querySelector('#modal-recaptcha') as HTMLElement;

                        const p = new Phone(phone);

                        let lineItemIndex = cartItem.order?.orderLineItems?.indexOf(lineItem) ?? cartItem.quote?.quoteLineItems?.indexOf(lineItem);
                        if (lineItemIndex) {
                            lineItemIndex += 1;
                        } else {
                            lineItemIndex = 1;
                        }
                        const lineItemOutput = lineItemIndex ? lineItemIndex.toString() : 'Undefined';

                        if (sess) {
                            fullName.value = `${sess.firstName || ''} ${sess.lastName || ''}`;
                            email.value = sess.email;
                            void p.Iti.promise.then(() =>
                                p.Iti.setNumber(sess.phone));
                        }

                        new Modal({
                            title: 'Item Removal Request',
                            outsideClosable: false,
                            size: 'modal-lg',
                            body,
                            onSubmit: () => {
                                const contactData: contact.CreateContactOpts = {
                                    name: fullName.value,
                                    phone: p,
                                    email: email.value,
                                    company: sess?.company || '',
                                    content:
                                        `Item removal request: ${cartItem.quote ? `quote #${cartItem.quote.serial ?? ''}` : `order #${cartItem.order?.invoiceNumber ?? ''}`}

                                    For line item #${lineItemOutput}

                                    Other comments:
                                    ${message.value.trim() || 'None'}`,
                                    $reCaptchaPlaceholder,
                                };
                                return contact.CreateContact(contactData).then(() => {
                                    toast.Success('Your request has been submitted!');
                                });
                            },
                        });
                    });

                    changeQty.addEventListener('click', e => {
                        e.preventDefault();
                        return;
                    });
                } else {
                    changeQty.addEventListener('click', e => {
                        e.preventDefault();
                        return;
                    });
                    remove.addEventListener('click', e => {
                        e.preventDefault();

                        const hide = () => lineItemEl.classList.add('d-none');
                        const show = () => lineItemEl.classList.remove('d-none');

                        const deleteItem = async () => {
                            hide();
                            return axios.delete(`/cart-items/${cartItem.id ?? ''}/line-items/${lineItem.id ?? ''}`).
                                then(async () => {
                                    await drawCart();

                                    const undoLinkID = `undo-${UUID()}`;
                                    const t = toast.Success(`Removed from cart. <a href="${Relref('/cart')}" class="text-white text-decoration-underline" id="${undoLinkID}">Undo</a>`);

                                    const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
                                    undo.addEventListener('click', e => {
                                        e.preventDefault();
                                        t?.dispose();
                                        return restore();
                                    });
                                }).
                                catch(err => {
                                    toast.Error('Something went wrong!');
                                    show();
                                    throw err;
                                });
                        };

                        const restore = async () => {
                            show();
                            return axios.patch(`/cart-items/${cartItem.id ?? ''}/line-items/${lineItem.id ?? ''}/active`, {
                                'active': true,
                            }).
                                then(() => {
                                    void drawCart();
                                    toast.Success('Restored item.');
                                }).
                                catch(err => {
                                    toast.Error('Something went wrong!');
                                    hide();
                                    throw err;
                                });
                        };

                        void deleteItem();
                    });
                }
            }

            if (lineItem.invalidCustomQuantity) {
                lineItemEl.querySelector<HTMLElement>('.cart-item-invalid-quantity')?.classList.remove('d-none');
                const editBelowMin = lineItemEl.querySelector<HTMLElement>('.edit-below-min');
                editBelowMin?.addEventListener('click', async e => {
                    e.preventDefault();
                    await PromptQuantityRequest({
                        quote: cartItem.quote,
                        order: cartItem.order,
                        lineItem,
                    });
                });
            }

            return lineItemEl;
        };

        const cartItemDescription = cartItemEl.querySelector('.cart-item-description') as HTMLElement;

        const proofs: models.Proof[] = [];
        const shownProofs = new Set<string>();

        let productCount = 0;
        const objectLineItems = cartItem.quote?.quoteLineItems || cartItem.order?.orderLineItems;
        if (objectLineItems) {
            for (const objectLineItem of objectLineItems) {
                if (objectLineItem.lineItemModel?.tax || objectLineItem.lineItemModel?.shipping) {
                    continue;
                }

                productCount++;

                if (productCount >= 2) {
                    break;
                }
            }
        }

        let invalidCustomQuantity = false;

        if (cartItem.quote) {
            title.textContent = `Quote ${cartItem.quote.serial ?? ''}`;

            if (cartItem.quote.quoteProofs) {
                for (const quoteProof of cartItem.quote.quoteProofs) {
                    if (!quoteProof.proof) {
                        continue;
                    }
                    quoteProof.proof.name = `Proof ${quoteProof.name ?? ''}`;
                    proofs.push(quoteProof.proof);
                }
            }

            if (cartItem.quote.quoteLineItems) {
                for (const quoteLineItem of cartItem.quote.quoteLineItems) {
                    if (quoteLineItem.lineItemModel?.tax || quoteLineItem.lineItemModel?.shipping) {
                        continue;
                    }

                    cartItemDescription.appendChild(drawCartItemLine(quoteLineItem));

                    if (quoteLineItem.quoteProof?.proof?.id) {
                        shownProofs.add(quoteLineItem.quoteProof?.proof.id);
                    }

                    if (quoteLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        } else if (cartItem.order) {
            title.textContent = `Reorder ${cartItem.order.invoiceNumber ?? 0}`;

            if (cartItem.order.orderProofs) {
                for (const orderProof of cartItem.order.orderProofs) {
                    if (!orderProof.proof) {
                        continue;
                    }
                    orderProof.proof.name = `Proof ${strings.TrimSuffix(orderProof.name || '', filepath.Ext(orderProof.name || ''))}`;
                    proofs.push(orderProof.proof);
                }
            }

            if (cartItem.order.orderLineItems) {
                for (const orderLineItem of cartItem.order.orderLineItems) {
                    if (orderLineItem.lineItemModel?.tax || orderLineItem.lineItemModel?.shipping) {
                        continue;
                    }

                    cartItemDescription.appendChild(drawCartItemLine(orderLineItem));

                    if (orderLineItem.orderProof?.proof?.id) {
                        shownProofs.add(orderLineItem.orderProof?.proof.id);
                    }

                    if (orderLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        }

        cartItemDescription.lastElementChild?.classList.remove('custom-border-bottom');

        if (invalidCustomQuantity) {
            checkoutButton.setAttribute('disabled', 'disabled');
        } else {
            checkoutButton.removeAttribute('disabled');
        }

        const footerProofs = cartItemEl.querySelector('.cart-item-footer-proofs') as HTMLElement;
        let footerHasProofs = false;

        for (const proof of proofs) {
            if (proof.id && shownProofs.has(proof.id)) {
                continue;
            }

            const proofLink = cartItemProofTemplate.cloneNode(true) as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id ?? ''}`);
                proofLink.setAttribute('data-caption', proof.name || '');

                proofImg.src = proof.thumbnail || proof.main;

                proofImg.title = proof.name || '';
                proofImg.alt = proof.name || '';
            } else {
                proofLink.parentElement?.removeChild(proofLink);
            }

            footerProofs.appendChild(proofLink);
            footerHasProofs = true;
        }

        if (!footerHasProofs) {
            footerProofs.parentElement?.removeChild(footerProofs);
        }

        return cartItemEl;
    };

    const drawCart = async () => {
        const cartID = carts.GetCartID();

        if (!cartID) {
            showEmptyCart();
            return;
        }

        checkoutButton.removeAttribute('disabled');
        const subtotal = document.querySelectorAll('.cart-subtotal');

        const cart = await carts.GetCart();

        subtotal.forEach(el => el.textContent = DollarFormat(cart?.total || '0'));

        if (!cart?.cart.cartItems?.length) {
            showEmptyCart();
            return;
        } else {
            const link = document.querySelector<HTMLElement>('.link');
            link?.classList.remove('d-none');
        }

        spinner.parentElement?.removeChild(spinner);

        const frag = document.createDocumentFragment();

        let i = 0;
        for (const cartItem of cart.cart.cartItems) {
            if (i++) {
                const hr = document.createElement('hr') as HTMLElement;
                hr.classList.add('m-0');
                frag.appendChild(hr);
            }

            frag.appendChild(drawCartItem(cartItem));
        }

        cartCard.innerHTML = '';
        cartCard.appendChild(frag);
    };

    void drawCart();
}